import * as Y from "yjs";
import {useState, useSyncExternalStore} from "react";
import {NodeSide, VisionBoardNode} from "../../cdx-models/utils/extended-fields";
import {clampButKeepRatio} from "../../lib/math-utils";
import {adaptToGrid, centerOnViewport, TwoNums, VisionBoardBag} from "./canvas-utils";
import {NodeBushEntry} from "./useVisionBoardData";
import {Card, CardId} from "../../cdx-models/Card";
import {CARD_HEIGHT, CARD_WIDTH} from "../../components/Card/card.css";
import {textNodeWasJustCreated} from "./widgets/TextWidget";
import {SearchFilter} from "../search/search-types";
import {Query} from "../../cdx-models/utils/MakeModel";
import {createDbFilters} from "../search/useSearch";

type CreationAnchor = {side: NodeSide; pos: TwoNums};

// if side
const getAnchorPos = ({side, pos}: CreationAnchor, nodeDimensions: TwoNums): TwoNums => {
  const [x, y] = pos;
  const [w, h] = nodeDimensions;
  switch (side) {
    case "left":
      return [x, y - h / 2];
    case "right":
      return [x - w, y - h / 2];
    case "top":
      return [x - w / 2, y];
    case "bottom":
      return [x - w / 2, y - h];
  }
};

const TEXT_DIMS: TwoNums = [160, 100];

export const createTextNode = (bag: VisionBoardBag, opts: {anchor?: CreationAnchor} = {}) => {
  const getPos = () => {
    if (opts.anchor) {
      const [x, y] = adaptToGrid(getAnchorPos(opts.anchor, TEXT_DIMS), bag.store);
      return {x, y, w: TEXT_DIMS[0], h: TEXT_DIMS[1], parent: null};
    } else {
      return centerOnViewport(bag, TEXT_DIMS);
    }
  };
  const node = bag.store.addNode({
    type: "text",
    value: {content: "New Node"},
    pos: getPos(),
  });
  textNodeWasJustCreated(node.id);
  return node;
};

export const createCardNode = (bag: VisionBoardBag, cardId: CardId) => {
  return bag.store.addNode({
    type: "card",
    value: {id: cardId, mode: "mini"},
    pos: centerOnViewport(bag, [CARD_WIDTH, CARD_HEIGHT]),
  });
};

const FILTER_DIMS = [120, 20] as TwoNums;

export const createFilterNode = (bag: VisionBoardBag) => {
  const node = bag.store.addNode({
    type: "filter",
    value: {queryId: null, mode: "horBar"},
    pos: centerOnViewport(bag, FILTER_DIMS),
  });
  // textNodeWasJustCreated(node.id);
  return node;
};

export const createImageNode = (bag: VisionBoardBag, [width, height]: [number, number]) => {
  const inputDims = clampButKeepRatio(
    [width, height],
    [
      (bag.canvasMeasureRef.current[0] * 0.8) / bag.posRef.current.scale,
      (bag.canvasMeasureRef.current[1] * 0.8) / bag.posRef.current.scale,
    ]
  );
  return bag.store.addNode({
    type: "image",
    value: {url: null, width, height},
    pos: centerOnViewport(bag, inputDims),
  });
};

export const duplicateVisionBoardNode = (
  bag: VisionBoardBag,
  node: VisionBoardNode,
  pos?: TwoNums
) => {
  const getNextPos = () => {
    const prevPos = node.pos;
    const [nextX, nextY] = pos || adaptToGrid([prevPos.x + 20, prevPos.y + 20], bag.store);
    return {...prevPos, x: nextX, y: nextY};
  };
  switch (node.type) {
    case "text": {
      return bag.store.addNode({
        type: "text",
        value: {...node.value},
        pos: getNextPos(),
      });
    }
    case "image": {
      return bag.store.addNode({
        type: "image",
        value: {...node.value},
        pos: getNextPos(),
      });
    }
    case "card": {
      return bag.store.addNode({
        type: "card",
        value: {...node.value},
        pos: getNextPos(),
      });
    }
    case "filter": {
      return bag.store.addNode({
        type: "filter",
        value: {...node.value},
        pos: getNextPos(),
      });
    }
    default: {
      throw Error(`Unknown node type: '${(node as any).type}'`);
    }
  }
};
export const duplicateBushEntryNode = (bag: VisionBoardBag, entry: NodeBushEntry) => {
  return duplicateVisionBoardNode(bag, entry.node.toJSON() as VisionBoardNode);
};

const createNodeDataObserver = (nodeMap: Y.Map<any>) => {
  let snapshot = nodeMap.toJSON() as VisionBoardNode;
  return {
    subscribe: (cb: () => void) => {
      const onUpdate = () => {
        snapshot = nodeMap.toJSON() as VisionBoardNode;
        cb();
      };
      nodeMap.observe(onUpdate);
      return () => nodeMap.unobserve(onUpdate);
    },
    getSnapshot: () => snapshot,
  };
};

export const useNodeData = (nodeMap: Y.Map<any>) => {
  const [observer] = useState(() => createNodeDataObserver(nodeMap));
  return useSyncExternalStore(observer.subscribe, observer.getSnapshot);
};

export const createFilterNodeQuery = (filter: SearchFilter[]): Query<Card> => ({
  ...createDbFilters(filter, {includeArchivedCards: true, forceOr: true}),
  $or: [{status: "done", visibility: ["archived", "default"]}, {visibility: "default"}],
});
